/**
 * Tablet/mobile modal for the flat per-SKU variant list.
 *
 * On viewports <= 989px the inline list is hidden by CSS and a single
 * "カートに追加する" trigger button opens this modal. To avoid duplicate
 * form ids in the DOM, we render the variant list only ONCE on the page
 * (in the desktop slot). When the modal opens, we move the list DOM
 * into the modal body slot. When it closes, we move it back.
 *
 * After a successful cart add (Dawn publishes PUB_SUB_EVENTS.cartUpdate)
 * the modal closes itself so the cart drawer is visible.
 */
class SkuListHost extends HTMLElement {
  connectedCallback() {
    this.modal = this.querySelector('.sku-list-modal');
    if (!this.modal) return;

    this.desktopSlot = this.querySelector('[data-sku-list-slot="desktop"]');
    this.modalSlot = this.querySelector('[data-sku-list-slot="modal"]');
    if (!this.desktopSlot || !this.modalSlot) return;

    // Move the modal element directly under <body> so its position:fixed
    // is anchored to the viewport, not to any ancestor with a transform
    // / sticky / will-change property (Dawn's product__column-sticky and
    // scroll-trigger animations both create such ancestors).
    this.modalOriginalParent = this.modal.parentElement;
    document.body.appendChild(this.modal);

    // Delegate click on document so trigger and close buttons inside the
    // host work, and overlay/close buttons inside the now-detached modal
    // also work.
    this.documentClickHandler = this.onClick.bind(this);
    document.addEventListener('click', this.documentClickHandler);

    // Auto-close on successful cart add
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      this.unsubscribeCartUpdate = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        if (!this.modal.classList.contains('is-open')) return;
        // Defer slightly so the cart drawer animation has room to start
        setTimeout(() => this.close(), 50);
      });
    }

    // Close on Escape
    this.escHandler = (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this.escHandler);
  }

  disconnectedCallback() {
    if (this.unsubscribeCartUpdate) this.unsubscribeCartUpdate();
    if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
    if (this.documentClickHandler) document.removeEventListener('click', this.documentClickHandler);
    // Move the modal back so it doesn't leak outside its host on
    // section re-render.
    if (this.modal && this.modalOriginalParent) {
      this.modalOriginalParent.appendChild(this.modal);
    }
  }

  onClick(event) {
    // Trigger lives inside the host
    const triggerBtn = event.target.closest('[data-sku-list-open]');
    if (triggerBtn && this.contains(triggerBtn)) {
      event.preventDefault();
      this.open();
      return;
    }
    // Close buttons live inside the (now-detached) modal
    const closeBtn = event.target.closest('[data-sku-list-close]');
    if (closeBtn && this.modal.contains(closeBtn)) {
      event.preventDefault();
      this.close();
    }
  }

  moveList(toSlot) {
    const list = this.desktopSlot.querySelector('.sku-list')
      || this.modalSlot.querySelector('.sku-list');
    if (list && list.parentElement !== toSlot) {
      toSlot.appendChild(list);
    }
  }

  open() {
    this.moveList(this.modalSlot);
    this.modal.hidden = false;
    this.modal.classList.add('is-open');
    document.body.classList.add('sku-list-modal-open');
    const closeBtn = this.modal.querySelector('.sku-list-modal__close');
    closeBtn?.focus();
  }

  close() {
    this.modal.classList.remove('is-open');
    this.modal.hidden = true;
    document.body.classList.remove('sku-list-modal-open');
    this.moveList(this.desktopSlot);
    const trigger = this.querySelector('[data-sku-list-open]');
    trigger?.focus();
  }
}

if (!customElements.get('sku-list-host')) {
  customElements.define('sku-list-host', SkuListHost);
}
